import { TabsContent } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'

import { LidoWithdrawalTab } from '@/components/opportunity/actionTab/lidoClaim'
import type { TabConfig } from '@/components/opportunity/useTabsConfig'
import { useTransaction } from '@/hooks/useTransaction'
import { type Action, useActionsQuery } from '@/queries/actions.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { OpportunityLoader } from '@/routes/opportunity.$network.$vaultId'
import type { CrosschainOption } from '@/types/crosschainOption'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { useState } from 'react'
import { useLoaderData } from 'react-router'
import type { Address } from 'viem'
import { formatUnits } from 'viem'
import { CrosschainAssetSelect } from '../sidePanel/crosschainAssetSelect'
import { DepositLimitWarning } from '../sidePanel/depositLimitWarning'
import { TxSteps } from '../sidePanel/txSteps'
import { useDepositLimit } from '../sidePanel/useDepositLimit'
import { ActionNotes } from './actionNotes'
import { AmountInputSection } from './amountInputSection'
import { ClaimRewardsTab } from './claimRewardsTab'
import { EthenaWithdrawalTab } from './ethenaClaim'
import { EtherfiClaimWithdrawTab } from './etherfiClaimWithdrawTab'
import { MellowDepositTab } from './mellowDepositTab'
import { MellowPendingRedeemTab } from './mellowPendingRedeemTab'
import { SyrupWaitForRedeemTab } from './syrupWaitForRedeemTab'
import { UpshiftWithdrawalTab } from './upshiftClaim'

interface Props {
  account: Address | undefined
  stepData: StepData
  tabConfig: TabConfig
  setDisplayFundWallet: (value: boolean) => void
  transactionsContext: TransactionsContext
  setTransactionFinished: ({ txHash }: { txHash: string | undefined; amount: string }) => void
  isExternalClaimAvailable?: boolean
  txState: 'idle' | 'loading' | 'success'
  crosschainOptions?: CrosschainOption[]
  selectedInput?: CrosschainOption
  setSelectedInput?: (input: CrosschainOption) => void
}

export const ActionTab = ({
  account,
  stepData,
  tabConfig,
  setDisplayFundWallet,
  transactionsContext,
  setTransactionFinished,
  txState,
  crosschainOptions,
  selectedInput,
  setSelectedInput,
}: Props) => {
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()
  const { vault } = useLoaderData<OpportunityLoader>()
  const { getParsedAmount, label, walletBalance, actionDescription, form } = tabConfig
  const { network, protocol, asset } = vault
  const inputAmount = form.watch('amount')
  const parsedAmount = getParsedAmount(inputAmount || '0')
  const [useAllFunds, setUseAllFunds] = useState(() => !form.getValues('amount'))
  const availableBalance =
    stepData.name === 'request-redeem' && protocol.product === 'liquid boring vault'
      ? String(
          BigInt(walletBalance) -
            BigInt((transactionsContext?.vaultSpecificData as any)?.redeemState?.assetAmount ?? 0n)
        )
      : walletBalance

  const { data: actions, isFetching } = useActionsQuery({
    address: account,
    action: stepData.name as Action,
    amount: parsedAmount,
    vault,
    all: useAllFunds,
    ignoreAmount: stepData.name === 'claim-redeem',
  })

  const { executeTx, currentActionIndex } = useTransaction(network.name, actions, inputAmount, setTransactionFinished)

  const onSubmit = form.handleSubmit(async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    if (stepData.name === 'deposit' && BigInt(parsedAmount) > BigInt(availableBalance)) {
      setDisplayFundWallet(true)
      return
    }
    await executeTx()
  })

  const noInputAmount = parsedAmount === '0'
  const { depositLimit, depositLimitValue, isAboveDepositLimit } = useDepositLimit({
    transactionsContext,
    parsedAmount,
    assetDecimals: asset.decimals,
    isDepositAction: stepData.name === 'deposit',
  })
  const isSubmitDisabled = noInputAmount || isAboveDepositLimit
  const isSubmitLoading = isFetching || txState === 'loading'

  if ((protocol.name === 'lido' || protocol.name === 'swell') && stepData.name === 'claim-redeem') {
    return (
      <LidoWithdrawalTab
        account={account}
        stepData={stepData}
        form={form}
        onSubmit={onSubmit}
        isSubmitLoading={isSubmitLoading}
        vault={vault}
        transactionsContext={transactionsContext}
        currentStep={currentActionIndex}
      />
    )
  }

  if (protocol.name === 'upshift finance' && stepData.name === 'claim-redeem') {
    return (
      <UpshiftWithdrawalTab
        account={account}
        stepData={stepData}
        form={form}
        onSubmit={onSubmit}
        isSubmitLoading={isSubmitLoading}
        vault={vault}
        transactionsContext={transactionsContext}
        currentStep={currentActionIndex}
      />
    )
  }

  if (protocol.name === 'ether.fi' && stepData.name === 'claim-redeem') {
    return (
      <EtherfiClaimWithdrawTab
        account={account}
        vault={vault}
        stepData={stepData}
        transactionsContext={transactionsContext}
        setTransactionFinished={setTransactionFinished}
        txState={txState}
      />
    )
  }

  if (protocol.name === 'ethena' && stepData.name === 'claim-redeem') {
    return (
      <EthenaWithdrawalTab
        account={account}
        stepData={stepData}
        form={form}
        onSubmit={onSubmit}
        isSubmitLoading={isSubmitLoading}
        vault={vault}
        transactionsContext={transactionsContext}
        currentStep={currentActionIndex}
      />
    )
  }

  if (stepData.name === 'claim-rewards' && transactionsContext && transactionsContext.rewards?.claimable.length) {
    return (
      <ClaimRewardsTab
        account={account}
        stepData={stepData}
        onSubmit={onSubmit}
        isSubmitLoading={isSubmitLoading}
        vault={vault}
        claimableRewards={transactionsContext.rewards.claimable}
        currentStep={currentActionIndex}
      />
    )
  }

  if (protocol.name === 'mellow' && (stepData.name === 'request-deposit' || stepData.name === 'claim-deposit')) {
    return (
      <MellowDepositTab
        account={account}
        vault={vault}
        stepData={stepData}
        tabConfig={tabConfig}
        transactionsContext={transactionsContext}
        setTransactionFinished={setTransactionFinished}
        txState={txState}
      />
    )
  }

  if (protocol.name === 'mellow' && stepData.name === 'claim-redeem') {
    return (
      <MellowPendingRedeemTab
        account={account}
        vault={vault}
        stepData={stepData}
        tabConfig={tabConfig}
        transactionsContext={transactionsContext}
        setTransactionFinished={setTransactionFinished}
        txState={txState}
      />
    )
  }

  if (
    stepData.name === 'redeem' &&
    protocol.name === 'syrup' &&
    (transactionsContext?.vaultSpecificData as Record<string, any> | undefined)?.['claimData']
  ) {
    return <SyrupWaitForRedeemTab stepData={stepData} vault={vault} transactionsContext={transactionsContext} />
  }

  const isNonIdle = txState !== 'idle'

  const displayedBalance = selectedInput ? selectedInput.balanceNative.toString() : availableBalance

  return (
    <form onSubmit={onSubmit}>
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        <AmountInputSection
          asset={asset}
          form={form}
          inputAmount={inputAmount}
          availableBalance={displayedBalance}
          setUseAllFunds={setUseAllFunds}
          isNonIdle={isNonIdle}
          actionDescription={actionDescription}
          tokenSelector={
            crosschainOptions && selectedInput && setSelectedInput ? (
              <CrosschainAssetSelect
                asset={asset}
                network={network}
                selectedInput={selectedInput}
                crosschainOptions={crosschainOptions}
                setSelectedInput={setSelectedInput}
                className="w-26"
              />
            ) : null
          }
        />

        {isAboveDepositLimit && depositLimitValue && depositLimit !== undefined && (
          <DepositLimitWarning
            depositLimitValue={depositLimitValue}
            assetSymbol={asset.symbol}
            onClick={() => {
              form.setValue('amount', formatUnits(depositLimit, asset.decimals), { shouldValidate: true })
              setUseAllFunds(false)
            }}
          />
        )}

        <TxSteps
          account={account}
          stepData={stepData}
          activeStep={currentActionIndex}
          loading={isSubmitLoading}
          disabled={isSubmitDisabled}
          label={label}
          network={network.name}
          success={txState === 'success'}
        />

        <ActionNotes
          vault={vault}
          stepData={stepData}
          transactionsContext={transactionsContext}
          inputAmount={inputAmount}
        />
      </TabsContent>
    </form>
  )
}

export const hasProtocolException = (
  vault: Vault,
  stepData: StepData,
  transactionsContext: TransactionsContext
): boolean => {
  const { protocol } = vault
  if ((protocol.name === 'lido' || protocol.name === 'swell') && stepData.name === 'claim-redeem') return true
  if (protocol.name === 'upshift finance' && stepData.name === 'claim-redeem') return true
  if (protocol.name === 'ether.fi' && stepData.name === 'claim-redeem') return true
  if (protocol.name === 'ethena' && stepData.name === 'claim-redeem') return true
  if (stepData.name === 'claim-rewards' && Boolean(transactionsContext?.rewards?.claimable.length)) return true
  if (protocol.name === 'mellow' && (stepData.name === 'request-deposit' || stepData.name === 'claim-deposit')) {
    return true
  }
  if (protocol.name === 'mellow' && stepData.name === 'claim-redeem') return true
  if (
    stepData.name === 'redeem' &&
    protocol.name === 'syrup' &&
    Boolean((transactionsContext?.vaultSpecificData as Record<string, any> | undefined)?.['claimData'])
  ) {
    return true
  }
  return false
}
